import { TFunction } from 'i18next';

export interface FrameTriggerConfig<Type, ShouldShowParams, Localizations = never> {
  type: Type;
  shouldShow: (params: ShouldShowParams) => boolean;
  showAfterFrames: number;
  getPrompt: ({ t, localizations }: { t: TFunction; localizations: Localizations }) => string;
  hideAfterFrames: number;
}

export default class FrameTrigger<TriggerType, TriggerParams, Localizations = never> {
  private config: FrameTriggerConfig<TriggerType, TriggerParams, Localizations>;

  private showTriggeredCount = 0;
  private hideTriggeredCount = 0;

  constructor(config: FrameTriggerConfig<TriggerType, TriggerParams, Localizations>) {
    this.config = config;
  }

  public get isShowing(): boolean {
    return this.showTriggeredCount >= this.config.showAfterFrames;
  }

  public runTick(params: TriggerParams): void {
    if (!this.isShowing) {
      if (this.config.shouldShow(params)) {
        this.showTriggeredCount++;

        // Changed from hidden to visible, start counting when to hide
        if (this.showTriggeredCount >= this.config.showAfterFrames) {
          this.hideTriggeredCount = 0;
        }
      } else {
        this.showTriggeredCount = 0;
      }
    } else {
      if (!this.config.shouldShow(params)) {
        this.hideTriggeredCount++;

        // Changed from visible to hidden, reset counters
        if (this.hideTriggeredCount >= this.config.hideAfterFrames) {
          this.showTriggeredCount = 0;
        }
      } else {
        this.hideTriggeredCount = 0;
      }
    }
  }

  public getType(): TriggerType {
    return this.config.type;
  }

  public getPrompt({ t, localizations }: { t: TFunction; localizations?: Localizations }): string {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return this.config.getPrompt({ t, localizations });
  }
}
